import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { uniqBy } from 'lodash';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input, Notification } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import useDebouncedValue from 'LearningManager/reports/hooks/useDebouncedValue';
import {
	SEARCH_DEBOUNCE_MS,
	toServerSearch
} from 'LearningManager/helpers/search';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { DirectoryUser } from 'Services/Api/Directory/Types';
import { pluralLabel } from 'LearningManager/helpers/role';

const LIST_SCROLL_ID = 'lms-ca-au-individual-scroll';

const toSelectedUser = (user: DirectoryUser) => ({
	id: user.id,
	firstName: user.firstName,
	lastName: user.lastName,
	email: user.email
});

const Individual = observer(() => {
	const { t } = useTranslation();
	const { assignUsersStore } = useStores();
	const { users, usersTotal, hasMoreUsers, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);
	const { dropdownProps: propertyDropdownProps } = usePagedProperties(
		tenantId,
		isTenantResolved
	);

	const {
		draft: {
			customSetup: {
				selectedIndividualUsers,
				selectedIndividualPropertyIds,
				allIndividualPropertiesSelected
			}
		},
		toggleIndividualUserSelection,
		setIndividualUserSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');

	const serverSearch = toServerSearch(search);
	const debouncedSearch = useDebouncedValue(serverSearch, SEARCH_DEBOUNCE_MS);
	const hasPropertyScope =
		allIndividualPropertiesSelected || selectedIndividualPropertyIds.length > 0;

	useEffect(() => {
		if (allIndividualPropertiesSelected && !isTenantResolved) return;
		const list = document.getElementById(LIST_SCROLL_ID);
		if (list) list.scrollTop = 0;
		assignUsersStore
			.fetchUsersByProperty(selectedIndividualPropertyIds, debouncedSearch, {
				allProperties: allIndividualPropertiesSelected,
				tenantId
			})
			.catch(() => undefined);
	}, [
		selectedIndividualPropertyIds,
		allIndividualPropertiesSelected,
		debouncedSearch,
		isTenantResolved,
		tenantId
	]);

	const isLoading =
	status.fetchUsers === Status.Pending || serverSearch !== debouncedSearch;
	const isLoadingMore = status.fetchMoreUsers === Status.Pending;
	const hasLoadMoreFailed = status.fetchMoreUsers === Status.Failure;
	const isSelectingAll = status.fetchAllUsers === Status.Pending;

	const loadMoreUsers = () =>
		assignUsersStore.fetchNextUsersPage().catch(() => undefined);

	const selectedIds = useMemo(
		() => new Set(selectedIndividualUsers.map((u) => u.id)),
		[selectedIndividualUsers]
	);

	const resultCount = usersTotal;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const uuid = String(id);
		const user = users.find((u) => u.id === uuid);
		if (!user) return;
		if (checked !== selectedIds.has(uuid)) {
			toggleIndividualUserSelection(toSelectedUser(user));
		}
	};

	const isAllSelected =
		!hasMoreUsers &&
		users.length > 0 &&
		users.every((u) => selectedIds.has(u.id));

	const handleSelectAll = async () => {
		if (isSelectingAll) return;
		if (isAllSelected) {
			const listedIds = new Set(users.map((u) => u.id));
			setIndividualUserSelection(
				selectedIndividualUsers.filter((u) => !listedIds.has(u.id))
			);
			return;
		}

		let pool = users;
		if (hasMoreUsers) {
			try {
				const allUsers = await assignUsersStore.fetchAllUsers();
				if (!allUsers) return;
				pool = allUsers;
			} catch {
				Notification.error({
					description: t(
						'learning.assignUsers.options.individual.loadAllUsersError'
					)
				});
				return;
			}
		}

		setIndividualUserSelection(
			uniqBy(
				[...selectedIndividualUsers, ...pool.map(toSelectedUser)],
				'id'
			)
		);
	};

	return (
		<div id="lms-ca-assign-users-individual">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div
						id="lms-ca-au-individual-property-filter"
						className={styles.search}
					>
						<div className={styles.selectProp}>
							{t('learning.assignUsers.options.group.selectProperties')}
						</div>
						<MultiSelectDropdown
							{...propertyDropdownProps}
							searchable
							searchPlaceholder={t(
								'learning.assignUsers.options.group.searchProperties'
							)}
							placeholder={t(
								'learning.assignUsers.options.group.selectOption'
							)}
							onChange={(ids, allSelected) => {
								setAssignUsersPropertyFilter(
									'selectedIndividualPropertyIds',
									ids,
									allSelected
								);
							}}
							defaultSelectedIds={
								selectedIndividualPropertyIds
							}
							defaultAllSelected={
								allIndividualPropertiesSelected
							}
						/>
					</div>

					<Input.Search
						id="lms-ca-au-individual-search"
						className={styles.search}
						placeholder={t('learning.assignUsers.options.individual.searchByNameOrEmail')}
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>

			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={t(
						pluralLabel('learning.assignUsers.options.individual.selectIndividuals', resultCount),
						{ count: resultCount }
					)}
				/>

				{resultCount > 1 && (
					<div
						id="lms-ca-au-individual-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isSelectingAll
							? t('learning.assignUsers.options.individual.loadingUsers')
							: isAllSelected
								? t('learning.assignUsers.options.group.deselectAll')
								: t('learning.assignUsers.options.group.selectAll')}
					</div>
				)}
			</div>
			<InfinityScrollWrapper
				dataLength={users.length}
				hasMore={hasMoreUsers && !isLoading && !hasLoadMoreFailed}
				onLoadMore={loadMoreUsers}
				loading={isLoadingMore}
				scrollableId={LIST_SCROLL_ID}
				endMessageText=""
			>
			<div id="lms-ca-au-individual-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading && !hasPropertyScope && (
					<div className={styles.empty}>
						{t('learning.assignUsers.options.individual.selectPropertyToSeeUsers')}
					</div>
				)}
				{!isLoading &&
					hasPropertyScope &&
					users.length === 0 &&
					status.fetchUsers !== Status.Failure && (
						<div className={styles.empty}>
							{t('learning.assignUsers.options.individual.noUsersFound')}
						</div>
					)}
				{!isLoading && status.fetchUsers === Status.Failure && (
					<div className={styles.empty}>
						{t('learning.assignUsers.options.individual.loadUsersError')}
					</div>
				)}
				{users.map((user) => {
					const isChecked = selectedIds.has(user.id);
					return (
						<CheckBoxCard
							key={user.id}
							id={user.id}
							htmlId={`lms-ca-au-individual-${user.id}`}
							icon={
								<Icon
									type={
										isChecked
											? 'indiOptFilled'
											: 'individualOptIcon'
									}
								/>
							}
							title={`${user.firstName} ${user.lastName}`}
							desc={user.email}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
				{hasMoreUsers && !isLoading && hasLoadMoreFailed && (
					<div
						id="lms-ca-au-individual-load-more"
						className={styles.loadMore}
					>
						<span className={styles.empty}>
							{t('learning.assignUsers.options.individual.loadMoreUsersError')}
						</span>
						<div
							id="lms-ca-au-individual-load-more-retry"
							className={styles.selectAllBtn}
							onClick={loadMoreUsers}
						>
							{t('learning.assignUsers.options.individual.tryAgain')}
						</div>
					</div>
				)}
			</div>
			</InfinityScrollWrapper>
		</div>
	);
});

export default Individual;